/**
 * Banner rendered at the top of /admin/* pages. Confirms the cross-
 * subdomain Supabase session is reaching the scheduler and surfaces
 * who's signed in. The pages themselves are gated by useCurrentUser
 * (PR #24) — unauthenticated visitors get bounced to the portal /login,
 * non-admins see API endpoints return 403. This banner is the visible
 * confirmation of that posture.
 *
 * States:
 *   - loading        → renders nothing (don't flicker the admin pages)
 *   - signed-in admin → green strip with email + "Open portal" link
 *   - signed-in non-admin → red strip explaining they're not admin
 *                          (the page-level gate also blocks the API)
 *   - unauthenticated → grey strip; useCurrentUser caller has already
 *                       initiated the redirect to portal /login
 */

import { useCurrentUser } from '../lib/use-current-user';

const PORTAL_BASE = 'https://aaportal.annuityassociation.com';

export function AdminSessionBanner() {
  const state = useCurrentUser();

  if (state.status === 'loading') return null;

  if (state.status === 'unauthenticated') {
    return (
      <div className="admin-session-banner admin-session-banner-info">
        <span>Redirecting to portal sign-in…</span>
      </div>
    );
  }

  const { user } = state;
  if (!user.isAdmin) {
    return (
      <div className="admin-session-banner admin-session-banner-warn">
        <span>
          Signed in as <strong>{user.email}</strong> — your account doesn't have
          admin access. The data below won't load.
        </span>
        <a href={`${PORTAL_BASE}/dashboard`}>Back to portal →</a>
      </div>
    );
  }

  return (
    <div className="admin-session-banner admin-session-banner-ok">
      <span>
        Signed in as <strong>{user.email}</strong> · Admin
      </span>
      <a href={`${PORTAL_BASE}/dashboard`}>Open portal →</a>
    </div>
  );
}
